import React from "react";
import ApexCharts from "react-apexcharts";
import { ApexOptions } from 'apexcharts';
import Chart from '../lineChart';
import { defaultSeries, defaulCategories } from "../../const";
import loader from'../../../../assets/images/datahub-loader.svg';
import { supplierColors, supplierNameToId } from "../../const/supplierColorMapping";
import { useAuth } from "providers/CustomAuthProvider";

const PriceTrendMinMaxChart = ({title = 'Pricing Chart', series =[], categories=[], isLoading}) => {

  const { clientId } = useAuth();

  const getLineColor = () => {
    // if series is missing or empty, return default color
    if (!series || series.length === 0) return 'black';

    // Get the first series item (the supplier line)
    const supplierSeries = series[0];
    
    // Try to get supplierId directly from the series
    let supplierId = supplierSeries?.supplierId;
    
    // If supplierId is not available, try to look it up by supplier name
    if (!supplierId && supplierSeries?.name) {
      supplierId = supplierNameToId[supplierSeries.name];
    }
    
    // Look up the color using the supplierId
    const color = supplierColors[supplierId?.toUpperCase()] ?? 'black';
    return color;
  };

  const supplierLineColor = getLineColor();

  // if (!series || series.length === 0 || !categories || categories.length === 0) {
  if (isLoading != false) {
    return (
      <>
        <h4 className="text-center">{title}</h4>
        <div className="d-flex justify-content-center align-items-center mt-5 mb-5" style={{ height: '400px' }}>
          <img src={loader} height={'100px'} alt="Loading" />
        </div>
        <hr/>
      </>
    );
  }

  if (!series || series.length === 0 || !categories || categories.length === 0) {
    return (
      <>
        <h4 className="text-center">{title}</h4>
        <div className="d-flex justify-content-center align-items-center mt-5 mb-5" style={{ height: '400px' }}>
          No data available for the selected filters!
        </div>
        <hr/>
      </>
    );
  }

  const options: ApexOptions = {
    chart: {
      height: '500px', // Set height to 60% of the viewport height
      width: '100%',  // Set width to 100% of the container
      // type: 'mixed',
      zoom: {
        enabled: false
      },
      toolbar: {
        show: false
      },
      animations: {
        enabled: false,
        easing: 'linear',
        speed: 0,
        animateGradually: { enabled: false },
        dynamicAnimation: { enabled: false, speed: 0 }
      }
    },
    colors:[
      // '#FEB019',
      // '#00E396',
      // '#008FFB',
      // '#2F4F4F',
      // '#8A2BE2', // BlueViolet
      // '#20B2AA',  // LightSeaGreen
      // '#FEB019',
        supplierLineColor, // Orange
      '#FF6347', // Red (Tomato)
      '#32CD32', // Green (LimeGreen)
      '#1E90FF', // Blue
    ],
    // dataLabels: {
    //   enabled: false,
    //   formatter: function(val, opts) {
    //     console.log('formatter:', opts.w.globals.seriesNames[opts.seriesIndex]);
    //     return val + ' - ' + opts.w.globals.seriesNames[opts.seriesIndex];
    //   },
    //   style: {
    //     fontSize: '10px',
    //     colors: [
    //       '#1E90FF', // Blue
    //       '#FF6347', // Red (Tomato)
    //       '#32CD32', // Green (LimeGreen)
    //       '#8A2BE2', // BlueViolet
    //       '#FF1493', // DeepPink
    //       '#FFA500', // Orange
    //       '#FFD700', // Gold
    //       '#00FA9A', // MediumSpringGreen
    //       '#FF4500', // OrangeRed
    //       '#20B2AA'  // LightSeaGreen
    //     ]
    //   },
    // },
    stroke: {
      width: [3,2,2,2,2,1,1,1,1,1,1,1,1],
      curve: 'smooth',
      dashArray: [0,0,0,5,5,5,5,5,5,5,5,5]
    },
    title: {
      text: title,
      align: 'center'
    },
    tooltip: {
      shared: true,
      custom: function({ series, dataPointIndex, w }) {
        const dateLabel =
          w.globals.categoryLabels?.[dataPointIndex] ||
          w.globals.labels?.[dataPointIndex] ||
          "";

        const rows = (series || [])
          .map((seriesData, idx) => ({
            name: w.globals.seriesNames[idx],
            price: seriesData[dataPointIndex],
            color: w.config.colors?.[idx] || "#666",
          }))
          .filter((item) => item.price !== null && item.price !== undefined && !isNaN(item.price))
          .map(
            (item) => `
              <div style="display:flex; align-items:center; justify-content:space-between; gap:10px; padding:4px 0;">
                <div style="display:flex; align-items:center; gap:6px;">
                  <span style="display:inline-block;width:9px;height:9px;border-radius:50%;background:${item.color};"></span>
                  <span style="font-size:12px;color:#4a4a4a;">${item.name}</span>
                </div>
                <span style="font-weight:600;font-size:12px;">${typeof item.price === 'number' ? item.price.toFixed(5) : 'N/A'}</span>
              </div>
            `
          )
          .join("");

        return `
          <div style="padding:12px 14px; min-width:190px; border-radius:10px; background:#fff; box-shadow:0 10px 25px rgba(0,0,0,0.18); border:1px solid rgba(0,0,0,0.04);">
            <div style="font-size:12px; font-weight:700; color:#2a2a2a; margin-bottom:8px;">${dateLabel}</div>
            ${rows || "<div style='font-size:12px;color:#999;'>No data</div>"}
          </div>
        `;
      }
    },
    legend: {
      // tooltipHoverFormatter: function(val, opts) {
      //   return val + ' - <strong>' + opts.w.globals.series[opts.seriesIndex][opts.dataPointIndex] + '</strong>'
      // },
      position: 'top',
      horizontalAlign: 'right',
      floating: true,
      offsetY: -25,
      offsetX: -5
    },
    markers: {
      size: 4,
      colors:['#fff'],
      strokeColors: '#000000',
      strokeWidth: 1,
    },
    xaxis: {
      categories: categories,
      title: {
        text: ''
      }
    },
    yaxis: { 
      title: {
        text: 'Price ($)'
      },
      // min: 0
    },
    plotOptions: {
      bar: {
        columnWidth: '75%',
        //distributed: true,
        //horizontal: false,
      }
    }
  };

  return (
      <Chart options={options} series={series} height="500px"/>
  );
};

export default PriceTrendMinMaxChart;
